import { useQuery, type QueryKey, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { useEffectiveClientId } from '@/contexts/AdminClientsContext';
import { moduleQueryRetry } from './moduleQueryRetry';

/** Options for {@link useModuleQuery}. */
export type ModuleQueryOptions<TQueryFnData, TData, TQueryKey extends QueryKey> = Omit<
  UseQueryOptions<TQueryFnData, Error, TData, TQueryKey>,
  'queryKey' | 'queryFn' | 'enabled'
> & {
  /** Builds the query key from the effective client id (see `createModuleQueryKeys`). */
  queryKey: (clientId: string | null) => TQueryKey;
  /** Performs the relay request; its response body becomes the query data. */
  request: () => Promise<AxiosResponse<TQueryFnData>>;
  /** Extra condition on top of "a client is selected", e.g. a required id being set. */
  enabled?: boolean;
};

/**
 * `useQuery` for module relay endpoints. Bundles the boilerplate every module
 * query repeats: resolves the effective client id, only runs once a client is
 * selected, unwraps `response.data` and applies {@link moduleQueryRetry} unless
 * `retry` is overridden.
 */
export function useModuleQuery<TQueryFnData, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>({
  queryKey,
  request,
  enabled = true,
  ...options
}: ModuleQueryOptions<TQueryFnData, TData, TQueryKey>): UseQueryResult<TData, Error> {
  const clientId = useEffectiveClientId();

  return useQuery({
    retry: moduleQueryRetry,
    ...options,
    queryKey: queryKey(clientId),
    queryFn: async () => {
      const response = await request();
      return response.data;
    },
    enabled: !!clientId && enabled,
  });
}
